import { create } from 'zustand';

/**
 * Global state for new-conversion notifications.
 *
 * - `lastSeenConversionId` is the high-water mark for "I've already shown
 *   alerts for everything ≤ this id". Persisted to localStorage so user doesn't
 *   get re-flashed for old conversions on reload.
 *
 * - `recentlyConverted` is a Map<sub_id, expiresAt_ms> tracking which sub_ids
 *   should be visually "lightning-flashing" right now. Components that render
 *   sub_ids subscribe to this map to apply the animation. Auto-cleared when
 *   expiresAt is in the past.
 *
 * - `audioPreference`: 'default' (built-in synth chime) or 'custom' (base64
 *   blob from user upload, stored in localStorage).
 */

const LS_LAST_ID = 'rj-alerts-last-id';
const LS_AUDIO_PREF = 'rj-alerts-audio-pref';
const LS_AUDIO_BLOB = 'rj-alerts-audio-blob';
const LS_AUDIO_VOLUME = 'rj-alerts-volume';
const LS_ENABLED = 'rj-alerts-enabled';
const LIGHTNING_DURATION_MS = 4_000;

function readLS<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    return raw == null ? fallback : (JSON.parse(raw) as T);
  } catch {
    return fallback;
  }
}

/**
 * Attempt to persist to localStorage. Returns true on success, false on failure
 * (QuotaExceededError or similar). Callers that care about storage success
 * (e.g. audio blob upload) can check the return value and surface the failure.
 */
function writeLS(key: string, value: unknown): boolean {
  try {
    localStorage.setItem(key, JSON.stringify(value));
    return true;
  } catch {
    // QuotaExceededError or SecurityError — value was not persisted
    return false;
  }
}

interface RecentSubFlash {
  subId: string;
  expiresAt: number;
}

interface AlertsState {
  lastSeenConversionId: number;
  recentlyConverted: RecentSubFlash[];
  audioPref: 'default' | 'custom';
  audioBlob: string | null; // base64 data URL
  audioVolume: number; // 0..1
  enabled: boolean;
  /** D11: set to true when the last setAudioBlob call failed to persist due to
   *  QuotaExceededError. Settings page reads this to show a storage-full warning. */
  audioBlobStorageFailed: boolean;

  setLastSeenConversionId: (id: number) => void;
  flashSubId: (subId: string) => void;
  /** Returns true if subId is currently flashing (call from component render). */
  isFlashing: (subId: string) => boolean;
  setAudioPref: (pref: 'default' | 'custom') => void;
  setAudioBlob: (blob: string | null) => void;
  setAudioVolume: (v: number) => void;
  setEnabled: (v: boolean) => void;
}

export const useAlertsStore = create<AlertsState>((set, get) => ({
  lastSeenConversionId: readLS<number>(LS_LAST_ID, 0),
  recentlyConverted: [],
  audioPref: readLS<'default' | 'custom'>(LS_AUDIO_PREF, 'default'),
  audioBlob: readLS<string | null>(LS_AUDIO_BLOB, null),
  audioVolume: readLS<number>(LS_AUDIO_VOLUME, 0.6),
  enabled: readLS<boolean>(LS_ENABLED, true),
  audioBlobStorageFailed: false,

  setLastSeenConversionId: (id) => {
    if (id <= get().lastSeenConversionId) return;
    set({ lastSeenConversionId: id });
    writeLS(LS_LAST_ID, id);
  },

  flashSubId: (subId) => {
    const now = Date.now();
    const expiresAt = now + LIGHTNING_DURATION_MS;
    set((state) => ({
      // Drop expired + replace existing entry for this subId. The replace is
      // critical for back-to-back conversions for same sub_id — we want the
      // visual to extend, not duplicate (and not get culled early by an older
      // setTimeout's cleanup pass).
      recentlyConverted: [
        ...state.recentlyConverted.filter((f) => f.subId !== subId && f.expiresAt > now),
        { subId, expiresAt },
      ],
    }));
    // Cleanup tick — only remove entries whose expiresAt is in the past.
    // Don't reference a single subId because back-to-back flashSubId calls for
    // the same sub_id should extend the TTL, and the OLDER setTimeout firing
    // shouldn't kill the newer entry. By comparing each entry's own expiresAt
    // we get correct behavior automatically.
    setTimeout(() => {
      const tnow = Date.now();
      set((state) => ({
        recentlyConverted: state.recentlyConverted.filter((f) => f.expiresAt > tnow),
      }));
    }, LIGHTNING_DURATION_MS + 100);
  },

  isFlashing: (subId) => {
    const now = Date.now();
    return get().recentlyConverted.some((f) => f.subId === subId && f.expiresAt > now);
  },

  setAudioPref: (pref) => {
    set({ audioPref: pref });
    writeLS(LS_AUDIO_PREF, pref);
  },
  setAudioBlob: (blob) => {
    set({ audioBlob: blob, audioBlobStorageFailed: false });
    if (blob) {
      // D11: track storage failure so Settings page can warn the user that the
      // audio file is too large for localStorage (QuotaExceededError). Without
      // this, the user sees "saved" but the audio vanishes on reload.
      const ok = writeLS(LS_AUDIO_BLOB, blob);
      if (!ok) set({ audioBlobStorageFailed: true });
    } else {
      localStorage.removeItem(LS_AUDIO_BLOB);
    }
  },
  setAudioVolume: (v) => {
    const clamped = Math.max(0, Math.min(1, v));
    set({ audioVolume: clamped });
    writeLS(LS_AUDIO_VOLUME, clamped);
  },
  setEnabled: (v) => {
    set({ enabled: v });
    writeLS(LS_ENABLED, v);
  },
}));
